// src/contexts/SharedCategoryContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import useAuthReset from '../hooks/useAuthReset';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

const SharedCategoryContext = createContext({
  sharedCategories: [],
  loading: false,
  error: null,
  createSharedCategory: async () => {},
  joinSharedCategory: async () => {},
  leaveSharedCategory: async () => {},
  regenerateInviteCode: async () => {},
  removeMember: async () => {},
  updateSettings: async () => {},
  fetchSharedCategories: async () => {},
  fetchSharedCategoryDetails: async () => {},
  fetchSharedCategorySpending: async () => {},
  convertExistingCategory: async () => {},
  deleteSharedCategory: async () => {},
});

export const useSharedCategory = () => {
  const context = useContext(SharedCategoryContext);
  if (!context) {
    throw new Error('useSharedCategory must be used within SharedCategoryProvider');
  }
  return context;
};

export const SharedCategoryProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [sharedCategories, setSharedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create authenticated API instance
  const getAuthApi = useCallback(() => {
    const authToken = token || localStorage.getItem('token');
    
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    return axios.create({
      baseURL: `${API_BASE_URL}/shared-categories`,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'x-auth-token': authToken
      }
    });
  }, [token]);

  // Fetch all shared categories for the current user
  const fetchSharedCategories = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.get('');
      
      // Backend returns {owned: [...], joined: [...]}
      // Combine them into one array
      const allCategories = [
        ...(response.data.owned || []),
        ...(response.data.joined || [])
      ];
      
      setSharedCategories(allCategories);
      return allCategories;
    } catch (err) {
      // 403 = plan doesn't include shared categories (family/business only) — not an error
      if (err.response?.status === 403) {
        setSharedCategories([]);
        return [];
      }
      console.error('Error fetching shared categories:', err);
      setError(err.response?.data?.message || 'Failed to fetch shared categories');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, getAuthApi]);

  // Fetch detailed information for a specific shared category
  const fetchSharedCategoryDetails = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.get(`/${id}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching shared category details:', err);
      setError(err.response?.data?.message || 'Failed to fetch category details');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Create a new shared category
  const createSharedCategory = useCallback(async (categoryData) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.post('/create', categoryData);
      
      // Add the new category to the state
      setSharedCategories(prev => [...prev, response.data.sharedCategory]);
      
      return response.data;
    } catch (err) {
      console.error('Error creating shared category:', err);
      setError(err.response?.data?.message || 'Failed to create shared category');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Join an existing shared category using invite code
  const joinSharedCategory = useCallback(async (inviteCode) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.post('/join', { inviteCode });
      
      // Add the joined category to the state
      setSharedCategories(prev => [...prev, response.data.sharedCategory]);
      
      return response.data;
    } catch (err) {
      console.error('Error joining shared category:', err);
      const errorMsg = err.response?.data?.message || 'Failed to join shared category';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Leave a shared category
  const leaveSharedCategory = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      await api.post(`/${id}/leave`);
      
      // Remove the category from state
      setSharedCategories(prev => prev.filter(cat => cat._id !== id));
      
      return true;
    } catch (err) {
      console.error('Error leaving shared category:', err);
      setError(err.response?.data?.message || 'Failed to leave shared category');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Regenerate invite code (owner only)
  const regenerateInviteCode = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.post(`/${id}/regenerate-code`);
      
      // Update the category in state with new invite code
      setSharedCategories(prev => 
        prev.map(cat => 
          cat._id === id 
            ? { ...cat, inviteCode: response.data.inviteCode, inviteCodeExpiry: response.data.inviteCodeExpiry }
            : cat
        )
      );
      
      return response.data;
    } catch (err) {
      console.error('Error regenerating invite code:', err);
      setError(err.response?.data?.message || 'Failed to regenerate invite code');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Remove a member from shared category (owner only)
  const removeMember = useCallback(async (id, userId) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.post(`/${id}/remove-member`, { memberId: userId });
      
      // Update the category in state
      setSharedCategories(prev => 
        prev.map(cat => 
          cat._id === id 
            ? { ...cat, members: response.data.sharedCategory.members }
            : cat
        )
      );
      
      return response.data;
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.response?.data?.message || 'Failed to remove member');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Update shared category settings (owner only)
  const updateSettings = useCallback(async (id, settings) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.put(`/${id}`, settings);
      
      // Update the category in state
      setSharedCategories(prev => 
        prev.map(cat => 
          cat._id === id 
            ? { ...cat, ...response.data.sharedCategory }
            : cat
        )
      );
      
      return response.data;
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.response?.data?.message || 'Failed to update settings');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Convert existing category to shared category
  const convertExistingCategory = useCallback(async (categoryName, budgetMode, transparencyLevel, sharedLimit) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      const response = await api.post('/convert-existing', {
        categoryName,
        budgetMode,
        transparencyLevel,
        sharedLimit
      });
      
      // Add the new shared category to the state
      setSharedCategories(prev => [...prev, response.data.sharedCategory]);
      
      // Refresh shared categories list to ensure we have the latest data
      await fetchSharedCategories();
      
      return response.data;
    } catch (err) {
      console.error('Error converting category:', err);
      const errorMsg = err.response?.data?.message || 'Failed to convert category';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [getAuthApi, fetchSharedCategories]);

  // Delete shared category (unshare) - owner only
  const deleteSharedCategory = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const api = getAuthApi();
      await api.delete(`/${id}`);
      
      // Remove from state
      setSharedCategories(prev => prev.filter(cat => cat._id !== id));
      
      return { success: true };
    } catch (err) {
      console.error('Error deleting shared category:', err);
      setError(err.response?.data?.message || 'Failed to unshare category');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthApi]);

  // Fetch shared category spending for a specific month
  const fetchSharedCategorySpending = useCallback(async (id, monthKey) => {
    try {
      const api = getAuthApi();
      const response = await api.get(`/${id}/spending?monthKey=${monthKey}`);
      return response.data.spending;
    } catch (err) {
      console.error('Error fetching shared category spending:', err);
      throw err;
    }
  }, [getAuthApi]);

  // Fetch shared categories on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchSharedCategories();
    } else {
      // Logged out → drop cached shared categories.
      setSharedCategories([]);
      setError(null);
    }
  }, [user, fetchSharedCategories]);

  // Privacy guard: drop cached shared-category memberships on identity
  // change so User A's groups do not flash to User B on a shared device.
  const resetSharedCategoryState = useCallback(() => {
    setSharedCategories([]);
    setError(null);
  }, []);
  useAuthReset(resetSharedCategoryState);

  const value = {
    sharedCategories,
    loading,
    error,
    createSharedCategory,
    joinSharedCategory,
    leaveSharedCategory,
    regenerateInviteCode,
    removeMember,
    updateSettings,
    fetchSharedCategories,
    fetchSharedCategoryDetails,
    fetchSharedCategorySpending,
    convertExistingCategory,
    deleteSharedCategory,
  };

  return (
    <SharedCategoryContext.Provider value={value}>
      {children}
    </SharedCategoryContext.Provider>
  );
};

export default SharedCategoryContext;
